import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoLinkHoverUnderline } from "@/shared/components";
import {
  DESCRIPTION_COLUMN_SIZING,
  qualifyPrincipal,
  QuickSearch,
  QuickSearchButton,
  renderDescription,
  renderPrincipal,
  ResourceView,
  useQuickSearchHotkey,
  useResourceView,
  type Filter,
  type FilterDefinition,
  type QuickSearchProperty,
} from "@/shared/components/ResourceView";
import { type AgentRecord } from "@/shared/domain/agents";
import { useAgents } from "@/shared/domain/agents/hooks";
import { ENTITY_ICONS } from "@/shared/foundation/icons";
import { Banner } from "@/shared/foundation/primitives";
import {
  cellStyles,
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  createColumnHelper,
  renderDashOrValue,
  useInMemoryRows,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
  type ICellRendererParams,
  type InMemorySortKey,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";

import styles from "./AgentsTab.module.css";
import { CreateAgentMenu } from "./CreateAgentMenu";

const columnHelper = createColumnHelper<AgentRecord>();

function colId<T>(col: ColDef<T>): string {
  return col.colId ?? col.field ?? "";
}

/**
 * Module-scope ColDefs so AG Grid sees a stable identity across renders.
 * Navigation lives on the Name cell because ``DataTable`` doesn't expose
 * ``onRowClicked``. Every column sets ``hide: false`` to override the
 * primitive's ``defaultColDef.hide: true`` (consumer opt-in pattern; see
 * ``AgentThreadsTable``).
 */
const columns = [
  columnHelper.accessor("agent_id", {
    headerName: "ID",
    minWidth: 150,
    sortable: true,
    hide: false,
    cellRenderer: (p: ICellRendererParams<AgentRecord, string>) =>
      p.value ? (
        <RobotoLinkHoverUnderline
          to={Goto.createAgentRoute(p.value)}
          className={cellStyles.idCell}
        >
          {p.value}
        </RobotoLinkHoverUnderline>
      ) : null,
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    minWidth: 200,
    sortable: true,
    hide: false,
    cellRenderer: (p: ICellRendererParams<AgentRecord, string | undefined>) => {
      const agent = p.data;
      if (!agent) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createAgentRoute(agent.agent_id)}
          className={cellStyles.primaryContent}
        >
          {agent.name}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    ...DESCRIPTION_COLUMN_SIZING,
    hide: false,
    cellRenderer: renderDescription,
  }),
  // A derived value needs the helper's accessor-function form; its field form
  // deliberately forbids a `valueGetter`.
  columnHelper.accessor((agent) => agent.variables.length, {
    colId: "variables",
    headerName: "Variables",
    width: 120,
    hide: false,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor((agent) => qualifyPrincipal(agent.created_by), {
    colId: "created_by",
    headerName: "Created by",
    width: 180,
    minWidth: 150,
    sortable: true,
    hide: false,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    width: 200,
    minWidth: 120,
    sortable: true,
    hide: false,
    variant: "relative",
  }),
  columnHelper.accessor((agent) => qualifyPrincipal(agent.modified_by), {
    colId: "modified_by",
    headerName: "Modified by",
    width: 180,
    minWidth: 150,
    sortable: true,
    hide: false,
    cellRenderer: renderPrincipal,
  }),
];

/**
 * Columns shown on a first visit. Everything in `columns` is offered by the
 * picker; anything left out of this list starts hidden. `ID` matters when
 * copying or matching one agent rather than scanning the list, and `Modified
 * by` is near-duplicate information next to `Created by` on most rows, so both
 * are available rather than always present.
 *
 * `<DataTable>` overlays these onto each ColDef's own `hide` whenever a seed
 * is supplied, so this list -- not `hide: false` above -- decides what the
 * grid renders at mount.
 */
const DEFAULT_VISIBLE_COLUMN_IDS = columns
  .map(colId)
  .filter((id) => id !== "agent_id" && id !== "modified_by");

const PICKER_OPTIONS: ColumnPickerOption[] = columns.map((column) => ({
  id: colId(column),
  label: column.headerName ?? colId(column),
}));

/**
 * How each sortable column compares two agents. The list endpoint takes no
 * sort parameter and `<DataTable>` sorts server-side by contract, so a header
 * without an entry here would move its arrow and reorder nothing -- keep this
 * map and the `sortable: true` flags above in step.
 */
const SORT_KEYS: Record<string, InMemorySortKey<AgentRecord>> = {
  agent_id: (agent) => agent.agent_id,
  name: (agent) => agent.name,
  created_by: (agent) => agent.created_by,
  modified: (agent) => new Date(agent.modified),
  modified_by: (agent) => agent.modified_by,
};

// Quick find routes an `agt_`-shaped value to the id and everything else to
// the name, as the Datasets list does. These are the only filters the page
// takes: the list endpoint filters nothing, so there is no Filter menu.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  { field: "agent_id", name: "ID", pattern: /^agt_\S+$/, comparator: "EQUALS" },
  { field: "name", name: "Name", fallback: true },
];

/** Definitions for the quick-find chip, so editing it keeps its comparator. */
const QUICK_FIND_DEFINITIONS: FilterDefinition[] = [
  {
    type: "string",
    field: "agent_id",
    name: "ID",
    allowedComparators: ["EQUALS"],
  },
  {
    type: "string",
    field: "name",
    name: "Name",
    allowedComparators: ["CONTAINS"],
  },
];

/** Whether `agent` is the one a quick find names: by exact ID, or by part of its name. */
function matchesQuickFind(agent: AgentRecord, filter: Filter): boolean {
  if (filter.type !== "string") {
    return true;
  }
  if (filter.field === "agent_id") {
    return filter.values.some((value) => agent.agent_id === value.trim());
  }
  return filter.values.some((value) =>
    agent.name.toLowerCase().includes(value.trim().toLowerCase()),
  );
}

/**
 * The "Agents" tab body, rendered inside the parent ``AgentsTabs``
 * container.
 */
export function AgentsTab() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const agentsQuery = useAgents({ orgId });
  const view = useResourceView<AgentRecord>({
    getRowId: (row) => row.agent_id,
    initialSort: { columnId: "modified", direction: "desc" },
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });
  const { table } = view;
  const quickSearch = useQuickSearchHotkey();

  const agents = React.useMemo(
    () => agentsQuery.data ?? [],
    [agentsQuery.data],
  );

  // The whole org's agents arrive in one response, so quick find, the
  // header's sort and the paging happen here; the list endpoint takes none.
  // The chip bar can add a second filter and switch to "Match any", so the
  // match mode decides whether a row needs every filter or any one of them.
  const filteredAgents = React.useMemo(() => {
    if (view.filters.type !== "structured") {
      return agents;
    }
    const { filters, matchMode } = view.filters;
    return agents.filter((agent) => {
      const matches = (filter: Filter) => matchesQuickFind(agent, filter);
      return matchMode === "OR"
        ? filters.some(matches)
        : filters.every(matches);
    });
  }, [agents, view.filters]);
  const { visibleRows, totalRowCount, hasNextPage } = useInMemoryRows(
    table,
    filteredAgents,
    SORT_KEYS,
  );

  const loading = deriveLoadingState({
    isLoading: agentsQuery.isLoading,
    isFetching: agentsQuery.isFetching,
  });

  if (agentsQuery.isError) {
    return (
      <Banner variant="error" className={styles.errorBanner}>
        Failed to load agents. {agentsQuery.error?.message ?? ""}
      </Banner>
    );
  }

  return (
    <>
      <QuickSearch
        open={quickSearch.isOpen}
        onClose={quickSearch.close}
        properties={QUICK_SEARCH_PROPERTIES}
        placeholder="Search by ID or name…"
        onSubmit={(filter) => {
          view.clearFilters();
          view.commit({ type: "structured", filter });
        }}
      />
      <div className={styles.tableSlot}>
        <ResourceView.Root>
          <ResourceView.Actions>
            <QuickSearchButton onClick={quickSearch.open} label="Find agents" />
            <ColumnVisibility
              options={PICKER_OPTIONS}
              visibleColumnIds={table.visibleColumnIds}
              setColumnVisibility={table.setColumnVisibility}
            />
            <div className={styles.actionsRight}>
              <CreateAgentMenu />
            </div>
          </ResourceView.Actions>
          <ResourceView.ActiveFilters
            filters={view.filters}
            filterDefinitions={QUICK_FIND_DEFINITIONS}
            onRemove={view.removeFilter}
            onUpdate={view.updateFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
          />
          <DataTable<AgentRecord>
            defaultColDef={FLEX_FILL_COL_DEF}
            table={table}
            // The list endpoint returns no ``total_count`` and nothing here
            // pages past its first response (up to 100 agents), so the total
            // is the rows on hand; the note above says when there are more.
            totalRowCount={totalRowCount}
            hasNextPage={hasNextPage}
            loading={loading}
            columns={columns}
            data={visibleRows}
            noRowsContent={
              <ResourceView.EmptyState
                hasActiveFilters={view.hasActiveFilters}
                noData={{
                  icon: ENTITY_ICONS.agent,
                  title: "No agents yet",
                  description:
                    "Save a prompt, goals and labels once so teammates can launch it on new datasets.",
                  action: (
                    <CreateAgentMenu variant="secondary" align="center" />
                  ),
                }}
                noMatches={{
                  icon: ENTITY_ICONS.agent,
                  title: "No matching agents",
                  description: "Try adjusting or clearing your filters.",
                }}
              />
            }
          />
        </ResourceView.Root>
      </div>
    </>
  );
}
